import React, { useEffect, useState, memo } from 'react';
import { TourStep } from '@/hooks/useTour';
import styles from './TourOverlay.module.scss';

interface TourOverlayProps {
  isActive: boolean;
  step: TourStep | null;
  stepNumber: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onComplete: () => void;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({
  isActive,
  step,
  stepNumber,
  totalSteps,
  onNext,
  onPrevious,
  onComplete,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  // Log when step changes (not on every render)
  useEffect(() => {
    if (isActive && step) {
      console.log(`[TourOverlay] Showing step ${stepNumber + 1}/${totalSteps}: ${step.id}`);
    }
  }, [step?.id, stepNumber]);

  useEffect(() => {
    if (!isActive || !step) {
      if (isActive === false && step === null) {
        console.log('[TourOverlay] Tour deactivated');
      }
      return;
    }

    // Always fade when navigating between steps
    console.log(`[TourOverlay] Step ${step.target} - updating`);
    // Don't clear targetRect - keep showing at old position while we measure new position

    console.log(`[TourOverlay] Looking for target: ${step.target}`);

    // Run action if provided (e.g., tab switch)
    if (step.action) {
      console.log('[TourOverlay] Executing step action');
      step.action();
    }

    // Find and measure target element - use delay for tab/layout transitions
    const initialTimer = setTimeout(() => {
      const element = document.querySelector(step.target);
      if (element) {
        // Check if element is actually visible and has dimensions
        const style = getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        const isVisible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          rect.width > 0 &&
          rect.height > 0;

        if (isVisible) {
          console.log(`[TourOverlay] Target is visible, scrolling into view`);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Wait for scroll animation to complete before measuring
          const measureTimer = setTimeout(() => {
            const newRect = element.getBoundingClientRect();
            console.log(
              `[TourOverlay] Found target at {x: ${newRect.x}, y: ${newRect.y}, w: ${newRect.width}, h: ${newRect.height}}`
            );
            setTargetRect(newRect);
          }, 300); // Increased to give more time for content to settle

          return () => clearTimeout(measureTimer);
        } else {
          console.warn(
            `[TourOverlay] ⚠️ Element found but not visible yet: ${step.target} (display: ${style.display}, rect: ${rect.width}x${rect.height})`
          );
        }
      } else {
        console.warn(`[TourOverlay] ⚠️ Target not found: ${step.target}`);
      }
    }, 300); // Increased to give more time for DOM to settle and tabs to load

    return () => clearTimeout(initialTimer);
  }, [isActive, step]);

  if (!isActive || !step || !targetRect) return null;

  // Calculate tooltip position
  const getTooltipPosition = () => {
    const padding = 20;
    const isMobile = window.innerWidth < 768;
    const tooltipWidth = isMobile ? 340 : 520;
    const tooltipHeight = 280;
    const position = step.position || 'bottom';

    let top = 0;
    let left = 0;

    const centerX = targetRect.left + targetRect.width / 2;
    const centerY = targetRect.top + targetRect.height / 2;

    switch (position) {
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = centerX - tooltipWidth / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + padding;
        left = centerX - tooltipWidth / 2;
        break;
      case 'left':
        top = centerY - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = centerY - tooltipHeight / 2;
        left = targetRect.right + padding;
        break;
      case 'center':
        top = window.innerHeight / 2 - tooltipHeight / 2;
        left = window.innerWidth / 2 - tooltipWidth / 2;
        break;
    }

    // Keep tooltip within viewport
    const maxLeft = window.innerWidth - tooltipWidth - 10;
    const maxTop = window.innerHeight - tooltipHeight - 10;
    
    return { 
      top: Math.max(10, Math.min(top, maxTop)), 
      left: Math.max(10, Math.min(left, maxLeft)) 
    };
  };

  const tooltipPos = getTooltipPosition();

  return (
    <>
      {/* Tooltip modal */}
      <div
        className={styles.tourTooltip}
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`,
        }}
      >
        <div className={styles.tourTopSection}>
          <div className={styles.tourIconColumn} aria-hidden="true">
            <span className={styles.tourIcon}>ⓘ</span>
          </div>
          <div className={styles.tourMainColumn}>
            <div className={styles.tourTitle}>
              <div className={styles.tourTitleText}>
                {step.superAdminOnly && <span className={styles.adminBadge}>👤 Super Admin</span>}
                {step.title}
              </div>
              <button
                className={styles.tourCloseButton}
                onClick={onComplete}
                aria-label="Close tour"
              >
                Close
              </button>
            </div>
            <div className={styles.tourContent}>{step.content}</div>
          </div>
        </div>
        <div className={styles.tourFooter}>
          <span className={styles.stepCounter}>
            {stepNumber + 1} of {totalSteps}
          </span>
          <div className={styles.tourButtons}>
            {stepNumber > 0 && (
              <button
                className={styles.tourButton}
                onClick={onPrevious}
                aria-label="Previous step"
              >
                ← Back
              </button>
            )}
            {stepNumber === totalSteps - 1 ? (
              <button
                className={`${styles.tourButton} ${styles.tourButtonPrimary}`}
                onClick={onComplete}
              >
                Complete Tour
              </button>
            ) : (
              <button
                className={`${styles.tourButton} ${styles.tourButtonPrimary}`}
                onClick={onNext}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(TourOverlay);
